import styled, { css } from 'styled-components';
import { makeBoxShadow } from '../../utils/make-box-shadow.js';
import { suppressProps } from '../../utils/suppress-props.js';
import { LoadingSpinner } from '../loading-spinner/index.js';
import { TextElement } from '../text/text.styles.js';
export const LoadingIcon = styled(LoadingSpinner)(({ theme, variant, }) => {
    const { inactive } = theme.styles.buttons.variant[variant];
    return css `
    color: ${theme.colors.text[inactive.textColor]};
  `;
});
export const ClipSpan = styled.span(({ theme }) => css `
    overflow-x: clip;
    text-overflow: ellipsis;
    line-height: ${theme.fontSizes[theme.styles.uiText.control.fontSize]};
  `);
const getButtonVariantStyles = ({ theme, variant = 'primary', isActive, }) => {
    const { colors, borderWidths, borderStyles, styles } = theme;
    const { inactive, active, pressed, hovered, focused } = styles.buttons.variant[variant];
    const activeStyle = css `
    background-color: ${active.backgroundColor &&
        colors.controls[active.backgroundColor]};
    color: ${active.textColor && colors.text[active.textColor]};
    box-shadow: none;
    border: ${borderStyles.none};
  `;
    const pressedBoxShadow = pressed.border
        ? `0px 0px 0px ${borderWidths.sm} inset ${String(colors.borders[pressed.border]) || ''}`
        : 'none';
    return css `
    background-color: ${colors.controls[inactive.backgroundColor]};
    color: ${colors.text[inactive.textColor]};
    box-shadow: ${inactive.boxShadow &&
        makeBoxShadow(inactive.boxShadow, theme)};
    ${isActive && activeStyle}
    &:hover {
      background-color: ${hovered.backgroundColor &&
        colors.controls[hovered.backgroundColor]};
      color: ${hovered.textColor && colors.text[hovered.textColor]};
    }
    &[aria-pressed] {
      background-color: ${pressed.backgroundColor &&
        colors.controls[pressed.backgroundColor]};
      box-shadow: ${pressedBoxShadow};
    }
    &:focus&:not([aria-disabled]) {
      background-color: ${focused.backgroundColor &&
        theme.colors.controls[focused.backgroundColor]};
      color: ${focused.textColor && theme.colors.text[focused.textColor]};
      box-shadow: none;
    }
    &:active {
      ${activeStyle}
    }
  `;
};
const getButtonDisabledStyles = ({ theme, isAriaDisabled, }) => css `
  &[disabled] {
    opacity: ${theme.opacity.disabled};
  }
  &[aria-disabled] {
    opacity: ${isAriaDisabled ? theme.opacity.disabled : theme.opacity.default};
    pointer-events: ${isAriaDisabled ? 'auto' : 'none'};
    cursor: ${isAriaDisabled ? 'not-allowed' : 'initial'};
  }
`;
const getPlainButtonStyles = ({ variant, theme }) => {
    if (variant !== 'plain')
        return css ``;
    return css `
    min-width: unset;
    height: ${theme.styles.icons.sizes.standard};
    padding: 0;
    &:hover {
      background-color: ${theme.colors.inherit};
      opacity: ${theme.opacity.faded};
    }
    &:active,
    &:focus {
      background-color: ${theme.colors.inherit};
    }
  `;
};
const getButtonWithIconStyles = ({ theme, startIcon, endIcon, hasChildren, }) => {
    if (!startIcon && !endIcon)
        return css ``;
    const hasIconsAndChildren = startIcon && hasChildren && endIcon;
    const baseStyle = `
  white-space: pre;
  line-height: ${theme.lineHeights.none};
  `;
    if (hasIconsAndChildren) {
        return css `
      ${baseStyle}
      padding: ${theme.space.x2};
    `;
    }
    return css `
    ${baseStyle}
    padding: ${theme.space.x2};
    ${startIcon &&
        `padding-right: ${hasChildren ? theme.space.x3 : theme.space.x2};`}
    ${endIcon &&
        `padding-left: ${hasChildren ? theme.space.x3 : theme.space.x2};`}
  `;
};
const getButtonWithLoadingStyles = ({ loading, theme, }) => {
    if (loading)
        return css `
      position: relative;
      color: ${theme.colors.transparent};
      &[disabled] {
        opacity: ${theme.opacity.default};
      }
    `;
    return css ``;
};
export const ButtonElement = styled.button.withConfig(suppressProps('loading', 'startIcon', 'endIcon', 'hasChildren', 'fullWidth', 'isActive', 'variant', 'isFocusVisible', 'isAriaDisabled'))(({ theme, fullWidth, disabled, isFocusVisible }) => {
    const { borderStyles, fontSizes, fontWeights, lineHeights, radii, fonts, letterSpacings, styles, opacity, outlines, } = theme;
    const { paddingY, paddingX } = styles.buttons.base;
    const lineHeight = lineHeights[styles.uiText.control.lineHeight];
    const height = `calc(${lineHeight} + ${paddingY} * 2)`;
    return css `
      display: flex;
      align-items: center;
      justify-content: center;
      border: ${borderStyles.none};
      border-radius: ${radii.x1_5};
      cursor: ${disabled ? 'not-allowed' : 'pointer'};
      font-family: ${fonts.body};
      font-size: ${fontSizes[styles.uiText.control.fontSize]};
      font-weight: ${fontWeights[styles.uiText.control.fontWeight]};
      line-height: ${lineHeight};
      letter-spacing: ${letterSpacings[styles.uiText.control.letterSpacing]};
      text-decoration: ${styles.uiText.control.textDecoration};
      padding: ${paddingY} ${paddingX};
      opacity: ${disabled ? opacity.disabled : opacity.default};
      width: ${fullWidth ? '100%' : 'min-content'};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: inherit;
      height: ${height};
      min-width: ${height};

      &:focus {
        outline: ${isFocusVisible ? outlines.control.rule : outlines.none};
        outline-offset: ${isFocusVisible ? outlines.control.offset : 0};
      }

      ${TextElement} {
        display: flex;
        align-items: center;
      }
    `;
}, getButtonVariantStyles, getButtonDisabledStyles, getButtonWithIconStyles, getPlainButtonStyles, getButtonWithLoadingStyles);
//# sourceMappingURL=button.styles.js.map